import { addToCart } from "@theme/utilities";
import { morphSection } from "@theme/section-renderer";

const OVERLAY_ID = "add-to-cart-link-overlay";
const PENDING_CLASS = "add-to-cart-link--pending";
/** Must match snippets/add-to-cart-link-pending.liquid (head script) — hides SSR cart until dismiss */
const FROM_LINK_GUARD_CLASS = "add-to-cart-link--from-link-pending";

/**
 * Collect cart section IDs from the DOM (same pattern as product-form).
 * @returns {string[]}
 */
function collectCartSectionIds() {
  const ids = new Set();
  for (const el of document.querySelectorAll("cart-items-component")) {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      ids.add(el.dataset.sectionId);
    }
  }
  return Array.from(ids);
}

/**
 * Shopify rejects empty `selling_plan`; strip when blank.
 * @param {Array<Record<string, unknown>>} items
 * @returns {Array<{ id: string | number; quantity: number; selling_plan?: string; properties?: Object }>}
 */
function normalizeLinkCartItems(items) {
  /** @type {Array<{ id: string | number; quantity: number; selling_plan?: string; properties?: Object }>} */
  const out = [];

  for (const item of items) {
    if (!item || typeof item !== "object") {
      continue;
    }

    const raw = /** @type {Record<string, unknown>} */ (item);
    const id = raw.id;
    const quantity = raw.quantity;

    if (id == null || quantity == null) {
      continue;
    }

    /** @type {{ id: string | number; quantity: number; selling_plan?: string; properties?: Object }} */
    const next = {
      id: typeof id === "string" || typeof id === "number" ? id : String(id),
      quantity: typeof quantity === "number" ? quantity : Number(quantity),
    };

    if (raw.selling_plan != null && raw.selling_plan !== "") {
      next.selling_plan = String(raw.selling_plan);
    }

    if (raw.properties && typeof raw.properties === "object") {
      next.properties = /** @type {Object} */ (raw.properties);
    }

    out.push(next);
  }

  return out;
}

function dismissAddToCartLinkOverlay() {
  document.documentElement.classList.remove(FROM_LINK_GUARD_CLASS);
  const main = document.getElementById("MainContent");
  main?.classList.remove(PENDING_CLASS);
  const overlay = document.getElementById(OVERLAY_ID);
  if (overlay) {
    overlay.setAttribute("aria-busy", "false");
    overlay.remove();
  }
}

/**
 * Morph returned section HTML into the live DOM.
 * @param {Record<string, string> | undefined} sections
 * @returns {Promise<void>}
 */
async function morphSectionsFromAddResponse(sections) {
  if (!sections || typeof sections !== "object") {
    return;
  }

  for (const sectionId of Object.keys(sections)) {
    const html = sections[sectionId];
    if (typeof html !== "string") {
      continue;
    }

    try {
      await morphSection(sectionId, html);
    } catch (error) {
      console.warn("[add-to-cart-link] morphSection failed", sectionId, error);
    }
  }
}

/**
 * Add items to cart from a link
 * Example: https://shop.bodi.com/cart?addItemsToCart={"items":[{"id":"1234567890","quantity":1}]}
 * @returns {Promise<void>}
 */
(async function addToCartLinkUtilModule() {
  const currentUrl = new URL(window.location.href);
  const rawParam = currentUrl.searchParams.get("addItemsToCart");

  if (!rawParam) {
    return;
  }

  /** @type {{ items?: unknown[], redirectUrl?: string } | null} */
  let addToCartAction = null;
  try {
    addToCartAction = JSON.parse(rawParam);
  } catch {
    dismissAddToCartLinkOverlay();
    return;
  }

  if (!addToCartAction || !Array.isArray(addToCartAction.items) || addToCartAction.items.length === 0) {
    dismissAddToCartLinkOverlay();
    return;
  }

  const items = normalizeLinkCartItems(
    /** @type {Array<Record<string, unknown>>} */ (addToCartAction.items)
  );

  if (items.length === 0) {
    dismissAddToCartLinkOverlay();
    return;
  }
  const sectionIds = collectCartSectionIds();
  const addOptions =
    sectionIds.length > 0
      ? {
          analyticsModule: "add-to-cart-link",
          sections: sectionIds,
          sections_url: window.location.pathname,
        }
      : { analyticsModule: "add-to-cart-link" };

  try {
    const result = await addToCart(items, addOptions);
    await morphSectionsFromAddResponse(
      result && typeof result === "object" && "sections" in result
        ? /** @type {{ sections?: Record<string, string> }} */ (result).sections
        : undefined
    );
  } catch (error) {
    console.error("[add-to-cart-link] Add to cart failed", error);
  } finally {
    dismissAddToCartLinkOverlay();
  }

  // Delay so tracking (GTM, Amplitude) and cart-listener can finish work after CartUpdateEvent
  await new Promise((resolve) => setTimeout(resolve, 2000));

  if (addToCartAction.redirectUrl) {
    window.location.assign(addToCartAction.redirectUrl);
    return;
  }

  currentUrl.searchParams.delete("addItemsToCart");
  const cleanUrl = currentUrl.toString();
  if (cleanUrl !== window.location.href) {
    window.history.replaceState(window.history.state ?? null, "", cleanUrl);
  }
})();
